import { z } from 'zod';

export const ChatTextDeltaSchema = z
  .object({
    type: z.literal('text-delta'),
    textDelta: z.string().optional(),
    delta: z.string().optional(),
  })
  .passthrough()
  .transform((value) => {
    const textDelta = value.textDelta ?? value.delta;
    if (typeof textDelta !== 'string') {
      throw new TypeError('text-delta event missing text payload');
    }
    return {
      ...value,
      textDelta,
    } satisfies { type: 'text-delta'; textDelta: string } & Record<string, unknown>;
  });

export const ChatToolCallSchema = z.object({
  type: z.literal('tool-call'),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
});

export const ChatToolResultSchema = z.object({
  type: z.literal('tool-result'),
  toolCallId: z.string(),
  result: z.unknown(),
});

export const ChatToolInputStartSchema = z.object({
  type: z.literal('tool-input-start'),
  toolCallId: z.string(),
  toolName: z.string(),
  providerExecuted: z.boolean().optional(),
  dynamic: z.boolean().optional(),
});

export const ChatToolInputAvailableSchema = z.object({
  type: z.literal('tool-input-available'),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
  providerExecuted: z.boolean().optional(),
  dynamic: z.boolean().optional(),
});

export const ChatToolInputErrorSchema = z.object({
  type: z.literal('tool-input-error'),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown().optional(),
  errorText: z.string(),
  providerExecuted: z.boolean().optional(),
  dynamic: z.boolean().optional(),
});

export const ChatToolInputDeltaSchema = z.object({
  type: z.literal('tool-input-delta'),
  toolCallId: z.string(),
  inputTextDelta: z.string(),
});

export const ChatToolOutputAvailableSchema = z.object({
  type: z.literal('tool-output-available'),
  toolCallId: z.string(),
  output: z.unknown(),
  providerExecuted: z.boolean().optional(),
  dynamic: z.boolean().optional(),
  preliminary: z.boolean().optional(),
});

export const ChatToolOutputErrorSchema = z.object({
  type: z.literal('tool-output-error'),
  toolCallId: z.string(),
  errorText: z.string(),
  providerExecuted: z.boolean().optional(),
  dynamic: z.boolean().optional(),
});

export const ChatStreamErrorSchema = z.object({
  type: z.literal('error'),
  error: z.string(),
});

export const ChatStreamTokenSchema = z
  .object({
    type: z.string().optional(),
    text: z.string().optional(),
  })
  .passthrough();

export const ChatParsedStreamDataSchema = z.union([
  ChatTextDeltaSchema,
  ChatToolCallSchema,
  ChatToolResultSchema,
  ChatToolInputStartSchema,
  ChatToolInputAvailableSchema,
  ChatToolInputErrorSchema,
  ChatToolInputDeltaSchema,
  ChatToolOutputAvailableSchema,
  ChatToolOutputErrorSchema,
  ChatStreamErrorSchema,
]);

export type ChatTextDelta = z.infer<typeof ChatTextDeltaSchema>;
export type ChatToolCall = z.infer<typeof ChatToolCallSchema>;
export type ChatToolResult = z.infer<typeof ChatToolResultSchema>;
export type ChatToolInputStart = z.infer<typeof ChatToolInputStartSchema>;
export type ChatToolInputAvailable = z.infer<typeof ChatToolInputAvailableSchema>;
export type ChatToolInputError = z.infer<typeof ChatToolInputErrorSchema>;
export type ChatToolInputDelta = z.infer<typeof ChatToolInputDeltaSchema>;
export type ChatToolOutputAvailable = z.infer<typeof ChatToolOutputAvailableSchema>;
export type ChatToolOutputError = z.infer<typeof ChatToolOutputErrorSchema>;
export type ChatStreamError = z.infer<typeof ChatStreamErrorSchema>;
export type ChatStreamToken = z.infer<typeof ChatStreamTokenSchema>;
export type ChatParsedStreamData = z.infer<typeof ChatParsedStreamDataSchema>;

export const StreamTokenSchema = ChatStreamTokenSchema;
export const ParsedStreamDataSchema = ChatParsedStreamDataSchema;

export type StreamToken = ChatStreamToken;
export type ParsedStreamData = ChatParsedStreamData;

export const TextDeltaSchema = ChatTextDeltaSchema;
export const ToolCallSchema = ChatToolCallSchema;
export const ToolResultSchema = ChatToolResultSchema;
export const ToolInputStartSchema = ChatToolInputStartSchema;
export const ToolInputAvailableSchema = ChatToolInputAvailableSchema;
export const ToolInputErrorSchema = ChatToolInputErrorSchema;
export const ToolInputDeltaSchema = ChatToolInputDeltaSchema;
export const ToolOutputAvailableSchema = ChatToolOutputAvailableSchema;
export const ToolOutputErrorSchema = ChatToolOutputErrorSchema;
export const StreamErrorSchema = ChatStreamErrorSchema;

export type TextDelta = ChatTextDelta;
export type ToolCall = ChatToolCall;
export type ToolResult = ChatToolResult;
export type ToolInputStart = ChatToolInputStart;
export type ToolInputAvailable = ChatToolInputAvailable;
export type ToolInputError = ChatToolInputError;
export type ToolInputDelta = ChatToolInputDelta;
export type ToolOutputAvailable = ChatToolOutputAvailable;
export type ToolOutputError = ChatToolOutputError;
export type StreamError = ChatStreamError;
